import { useState, useEffect } from 'react';
import { FiClock, FiUser, FiEdit2, FiChevronDown } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchUsers, updateTicket } from '../api/api';
import './TicketCard.css';

const TicketCard = ({ ticket, userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false);
  const [engineers, setEngineers] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState('');
  const [updating, setUpdating] = useState(false);

  const canChangeAssignment = userRole === 'admin' && 
    (ticket.status === 'open' || ticket.status === 'in-progress');

  useEffect(() => {
    if (showAssignmentDropdown && userRole === 'admin') {
      fetchUsers('engineer')
        .then((data) => setEngineers(Array.isArray(data) ? data : []))
        .catch(() => setEngineers([]));
    }
  }, [showAssignmentDropdown, userRole]);

  const handleQuickAssignment = async (engineerId) => {
    if (!engineerId) return;
    setUpdating(true);
    try {
      await updateTicket(ticket.id, { assigned_to: engineerId });
      setShowAssignmentDropdown(false);
      // Refresh the page to show updated assignment
      window.location.reload();
    } catch (err) {
      alert(err.message || 'Failed to update assignment');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'open':
        return 'status-open';
      case 'in progress':
      case 'in-progress':
        return 'status-progress';
      case 'resolved':
      case 'closed':
        return 'status-resolved';
      default:
        return 'status-open';
    }
  };

  const getStatusLabel = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'open':
        return 'Pending';
      case 'in progress':
      case 'in-progress':
        return 'In Progress';
      case 'resolved':
      case 'closed':
        return 'Completed';
      default:
        return 'Pending';
    }
  };

  const getPriorityColor = (priority) => {
    switch ((priority || 'medium').toLowerCase()) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return 'priority-medium';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  let basePath = '/user/tickets';
  if (location.pathname.startsWith('/admin')) basePath = '/admin/tickets';
  if (location.pathname.startsWith('/engineer')) basePath = '/engineer/tickets';
  if (location.pathname.startsWith('/customer')) basePath = '/customer/tickets';

  return (
    <div
      className="ticket-card"
      onClick={() => navigate(`${basePath}/${ticket.id}`)}
    >
      <div className="ticket-header">
        <div className="ticket-id">#{ticket.id}</div>
        <div className={`status-badge ${getStatusColor(ticket.status)}`}>
          {getStatusLabel(ticket.status)}
        </div>
      </div>
      <h3 className="ticket-title">{ticket.subject || ticket.title}</h3>
      <p className="ticket-description">{ticket.description}</p>
      <div className="ticket-footer">
        <div className="ticket-meta">
          <div className="meta-item">
            <FiUser className="meta-icon" />
            <span>{ticket.customerName || 'Customer'}</span>
          </div>
          {(ticket.assigned_to_name || ticket.assignedToName) && (
            <div className="meta-item assigned-engineer">
              <FiUser className="meta-icon assigned-icon" />
              <div className="assigned-info">
                <span className="assigned-label">Assigned to:</span>
                <span className="assigned-name">{ticket.assigned_to_name || ticket.assignedToName}</span>
              </div>
              {canChangeAssignment && (
                <div className="assignment-actions">
                  <button 
                    className="edit-assignment-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAssignmentDropdown(!showAssignmentDropdown);
                    }}
                    title="Change assignment"
                  >
                    <FiEdit2 />
                  </button>
                  {showAssignmentDropdown && (
                    <div className="quick-assignment-dropdown">
                      <select
                        value={selectedEngineer}
                        onChange={(e) => {
                          setSelectedEngineer(e.target.value);
                          handleQuickAssignment(e.target.value);
                        }}
                        className="engineer-select"
                        autoFocus
                      >
                        <option value="">Select engineer...</option>
                        {engineers.map((engineer) => (
                          <option key={engineer.id} value={engineer.id}>
                            {engineer.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="cancel-assignment-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAssignmentDropdown(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="meta-item">
            <FiClock className="meta-icon" />
            <span>{formatDate(ticket.createdAt)}</span>
          </div>
        </div>
        <div className={`priority-badge ${getPriorityColor(ticket.priority)}`}>
          {ticket.priority}
        </div>
      </div>
    </div>
  );
};

export default TicketCard;

